import type { ChatMessage, MessageBlock, TextBlock, HeadingBlock, FindingsMessageBlock } from "@/lib/chat-types";
import { createId, normalizeRole, parseTimestamp, readString, toRecord } from "@/lib/fund-compliance/parse-primitives";
import { extractAllAttachmentBlocks } from "@/lib/fund-compliance/parse-attachments";
import { extractStructuredFindingsFromText } from "@/lib/fund-compliance/parse-findings";
import { extractMetadataBlocks } from "@/lib/fund-compliance/parse-metadata";
import { stripRedundantReviewSections } from "@/lib/fund-compliance/strip-review-sections";
import { toText } from "@/lib/fund-compliance/message-factories";

/* ── Helpers ─────────────────────────────────────────────────────── */

function isReviewMode(record: Record<string, unknown>): boolean {
  const metadata = toRecord(record.metadata);
  return (
    readString(record.messageMode)?.toLowerCase() === "review" ||
    readString(record.mode)?.toLowerCase() === "review" ||
    readString(metadata?.messageMode)?.toLowerCase() === "review"
  );
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Normalize a list of raw message payloads (from REST history or WS history event)
 * into typed ChatMessage objects.
 */
export function normalizeHistoryMessages(items: unknown[]): ChatMessage[] {
  return items
    .map((item) => {
      const record = toRecord(item);
      if (!record) return null;

      const rawText = toText(record.content ?? record.parts ?? record.message ?? record.text);
      const attachmentBlocks = extractAllAttachmentBlocks(record);
      const metadata = extractMetadataBlocks(record.metadata);
      const hasStructuredReview = metadata.blocks.some((b) => b.type === "findings");

      // Strip narrative sections that will be rendered via structured review
      // blocks (findings grid / action bullets / notes) to avoid showing the
      // same content twice. No-op when metadata has no review data.
      const text = rawText ? stripRedundantReviewSections(rawText, metadata.reviewRecord) : rawText;

      const blocks: MessageBlock[] = [];

      if (text) {
        blocks.push({ type: "text", content: text } satisfies TextBlock);
      }

      blocks.push(...attachmentBlocks);

      // Only use regex fallback when no structured findings came from metadata.
      // Run against the original rawText so stripping does not hide fallback input.
      if (isReviewMode(record) && rawText && !hasStructuredReview) {
        const fallbackFindings = extractStructuredFindingsFromText(rawText);
        if (fallbackFindings.length > 0) {
          blocks.push({ type: "heading", content: "Structured Findings", level: 3 } satisfies HeadingBlock);
          blocks.push({ type: "findings", findings: fallbackFindings } satisfies FindingsMessageBlock);
        }
      }

      blocks.push(...metadata.blocks);

      if (blocks.length === 0) return null;

      const message: ChatMessage = {
        id: readString(record.id) ?? createId("history"),
        role: normalizeRole(record.role),
        blocks,
        suggestedFollowUps: metadata.suggestedFollowUps.length > 0 ? metadata.suggestedFollowUps : undefined,
        timestamp:
          parseTimestamp(record.createdAt) ||
          parseTimestamp(record.timestamp) ||
          parseTimestamp(record.updatedAt),
      };
      return message;
    })
    .filter((msg): msg is ChatMessage => msg !== null)
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Normalize a single raw message payload.
 */
export function normalizeSingleMessage(item: unknown): ChatMessage | null {
  return normalizeHistoryMessages([item])[0] ?? null;
}
